import { useCallback, useEffect, useRef, useState } from 'react';

type Settled<T> = { key: string; data: T | undefined; error: Error | undefined };

export type VssQuery<T> = {
  data: T | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => void;
};

/**
 * Runs `load` for `key` unless `skip`, and again on `refetch`; `key` has to change whenever `load` would ask for
 * something else. Loading holds until the first result for a key, a refetch keeps the last data and does not raise it.
 */
export const useVssQuery = <T>(key: string, load: () => Promise<T>, skip: boolean): VssQuery<T> => {
  const [settled, setSettled] = useState<Settled<T>>();
  const [round, setRound] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (skip) {
      return;
    }
    let current = true;
    loadRef.current().then(
      (data) => {
        if (current) {
          setSettled({ key, data, error: undefined });
        }
      },
      (error: unknown) => {
        if (current) {
          setSettled((last) => ({
            key,
            data: last?.key === key ? last.data : undefined,
            error: error instanceof Error ? error : new Error(String(error))
          }));
        }
      }
    );
    return () => {
      current = false;
    };
  }, [key, skip, round]);

  const refetch = useCallback(() => setRound((value) => value + 1), []);

  const result = !skip && settled?.key === key ? settled : undefined;
  return { data: result?.data, error: result?.error, loading: !skip && result === undefined, refetch };
};
